import { NeqTreeNode } from './neq-tree-node.model';
import { BehaviorSubject } from 'rxjs';
import { NeqTreeFlatNode } from './neq-tree-flat-node.model';

/**
 * A database that only load first level. children are loaded on parent click.
 */
export abstract class NeqTreeDatabase {

  dataChange = new BehaviorSubject<NeqTreeNode[]>([]);
  nodeMap = new Map<string, NeqTreeNode>();

  modifiedItems: NeqTreeNode[] = [];

  get data(): NeqTreeNode[] { return this.dataChange.value; }

  abstract initialize();

  protected abstract _canBeRoot(node: NeqTreeNode);

  public abstract canBeChildOf(node: NeqTreeNode, nodeParent: NeqTreeNode);

  protected abstract _loadMore(item: NeqTreeNode,itemFlat: NeqTreeFlatNode);

  getNode(identifier: string): NeqTreeNode {
    return this.nodeMap.get(identifier);
  }

  /** Expand a node  */
  loadMore(item: NeqTreeFlatNode, onlyFirstTime = false) {
    if (!this.nodeMap.has(item.identifier)) {
      return;
    }

    const itemTreeNode = this.nodeMap.get(item.identifier);
    if (onlyFirstTime && itemTreeNode.isLoaded) {
      return;
    }
    item.isLoading = true;
    this._loadMore(itemTreeNode, item);
  }

  getParentFromNodes(node: NeqTreeNode): NeqTreeNode {
    for (let currentRoot of this.data) {
      const parent = this.getParent(currentRoot, node);
      if (parent != null) {
        return parent;
      }
    }
    return null;
  }

  getParent(currentRoot: NeqTreeNode, node: NeqTreeNode): NeqTreeNode {
    if (currentRoot.children && currentRoot.children.length > 0) {
      for (let child of currentRoot.children) {
        if (child.identifier === node.identifier) {
          return currentRoot;
        } else if (child.children && child.children.length > 0) {
          const parent = this.getParent(child, node);
          if (parent != null) {
            return parent;
          }
        }
      }
    }
    return null;
  }

  detachFromParent(itemToDetach: NeqTreeNode) {
    const parentNodeOfItem = this.getParentFromNodes(itemToDetach);
    if (parentNodeOfItem) { // if not orphan
      parentNodeOfItem.childrenChange.value.splice(parentNodeOfItem.childrenChange.value.indexOf(itemToDetach), 1);
    } else { // if is root
      this.data.splice(this.data.indexOf(itemToDetach), 1);
    }
  }

  insertItem(parent: NeqTreeNode, itemToInsert: NeqTreeNode): NeqTreeNode {
    if (this.canBeChildOf(itemToInsert, parent)) {
      this.detachFromParent(itemToInsert);
      parent.childrenChange.value.unshift(itemToInsert);
      this.modifiedItems.push(itemToInsert);
    }
    this.dataChange.next(this.data);
    return itemToInsert;
  }

  insertItemAbove(node: NeqTreeNode, itemToInsert: NeqTreeNode): NeqTreeNode {
    const parentNode = this.getParentFromNodes(node);
    if (parentNode != null && this.canBeChildOf(itemToInsert, parentNode)) { // add child to parent
      this.detachFromParent(itemToInsert);
      parentNode.children.splice(parentNode.children.indexOf(node), 0, itemToInsert);
      this.modifiedItems.push(itemToInsert);
    } else if (parentNode == null && this._canBeRoot(itemToInsert)) { // add to root

      this.detachFromParent(itemToInsert);
      this.data.splice(this.data.indexOf(node), 0, itemToInsert);
      this.modifiedItems.push(itemToInsert);
    }

    this.dataChange.next(this.data);
    return itemToInsert;
  }

  insertItemBelow(node: NeqTreeNode, itemToInsert: NeqTreeNode): NeqTreeNode {
    const parentNode = this.getParentFromNodes(node);
    if (parentNode != null && this.canBeChildOf(itemToInsert, parentNode)) { // add child to parent
      this.detachFromParent(itemToInsert);
      parentNode.children.splice(parentNode.children.indexOf(node) + 1, 0, itemToInsert);
      this.modifiedItems.push(itemToInsert);
    } else if (parentNode === null && this._canBeRoot(itemToInsert)) { // add to root
      this.detachFromParent(itemToInsert);
      this.data.splice(this.data.indexOf(node) + 1, 0, itemToInsert);
      this.modifiedItems.push(itemToInsert);
    } else if (this.canBeChildOf(itemToInsert, node)) { // this node became his parent
      this.detachFromParent(itemToInsert);
      node.children.splice(0, 0, itemToInsert);
      this.modifiedItems.push(itemToInsert);
    }
    this.dataChange.next(this.data);
    return itemToInsert;
  }

  protected _generateNode(label: string, type: string, code: string, hasChildren: boolean): NeqTreeNode {

    const nodeIdentifier = NeqTreeNode.getNodeIdentifier(type, code);

    if (this.nodeMap.has(nodeIdentifier)) {
      return this.nodeMap.get(nodeIdentifier);
    }
    const result = new NeqTreeNode(label, type, code, hasChildren, false, false);
    this.nodeMap.set(result.identifier, result);
    return result;
  }
}
